import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiProviderGroupSpotService } from '../../../../../core/services/api/provider-parking/api-provider-group-spot.service';
import { ProviderGroupSpot } from '../../../../../core/models/provider-parking/provider-group-spot.model';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalProviderGroupSpotComponent } from "../../../modals/provider-parking/modal-provider-group-spot/modal-provider-group-spot.component";

@Component({
  selector: 'app-provider-group-spot',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalProviderGroupSpotComponent],
  templateUrl: './provider-group-spot.component.html',
  styleUrl: './provider-group-spot.component.css'
})
export class ProviderGroupSpotComponent implements OnInit {
  public providerGroups: ProviderGroupSpot[] = [];
  public filteredGroups: ProviderGroupSpot[] = [];
  public isLoading: boolean = false;

  // Management State
  public isModalOpen: boolean = false;
  public selectedGroup: ProviderGroupSpot | null = null;

  // Pagination State
  public currentPage: number = 1;
  public pageSize: number = 10;
  public totalItems: number = 0;
  public pageSizeOptions: number[] = [10, 20, 50, 100];

  // Search & Filter State
  public searchText: string = '';
  public isFilterDropdownOpen: boolean = false;
  public filterSite: string = '';
  public siteOptions: string[] = [];

  // Sorting State
  public sortColumn: string = '';
  public sortDirection: 'asc' | 'desc' = 'asc';

  // Dashboard Stats (Matching Standard Style)
  public stats = [
    { title: 'กลุ่มทั้งหมด', value: 0, icon: 'groups', color: 'text-blue-600 bg-blue-50' },
    { title: 'จุดให้บริการรวม', value: 0, icon: 'settings_input_component', color: 'text-emerald-600 bg-emerald-50' },
    { title: 'สถานที่ (Sites)', value: 0, icon: 'place', color: 'text-amber-600 bg-amber-50' },
    { title: 'จุดสูงสุดต่อกลุ่ม', value: 0, icon: 'trending_up', color: 'text-indigo-600 bg-indigo-50' }
  ];

  constructor(private apiProviderGroupSpotService: ApiProviderGroupSpotService) { }

  ngOnInit(): void {
    this.getProviderGroups();
  }

  public getProviderGroups(): void {
    this.isLoading = true;
    this.apiProviderGroupSpotService.getProviderGroup().subscribe({
      next: (res) => {
        this.providerGroups = res || [];
        if (this.providerGroups.length === 0) this.loadMockData();
        this.siteOptions = Array.from(new Set(this.providerGroups.map(g => g.site))).filter(s => s).sort();
        this.applyFilters();
        this.calculateStats();
        this.isLoading = false;
      },
      error: (err) => {
        this.loadMockData();
        this.siteOptions = Array.from(new Set(this.providerGroups.map(g => g.site))).filter(s => s).sort();
        this.applyFilters();
        this.calculateStats();
        this.isLoading = false;
      }
    });
  }

  private loadMockData(): void {
    this.providerGroups = [
      { id: 2, name: 'F4', servicePoints: ['Entry 4', 'Exit 3'], site: 'The Market' },
      { id: 17, name: 'Novotel', servicePoints: ['Entry 1', 'Entry 2', 'Entry 3', 'Entry 4'], site: 'The Market' },
      { id: 23, name: 'Around', servicePoints: ['AroundD Entry 1', 'AroundD Exit 1'], site: 'AroundD Lifestyle' },
      { id: 25, name: 'Main Gate', servicePoints: ['Gate 1', 'Gate 2'], site: 'The Market' },
      { id: 30, name: 'Basement', servicePoints: ['B1', 'B2', 'B3'], site: 'AroundD Lifestyle' }
    ];
  }

  private calculateStats(): void {
    this.stats[0].value = this.providerGroups.length;
    this.stats[1].value = this.providerGroups.reduce((acc, g) => acc + (g.servicePoints?.length || 0), 0);
    this.stats[2].value = this.siteOptions.length;
    this.stats[3].value = Math.max(...this.providerGroups.map(g => g.servicePoints?.length || 0), 0);
  }

  // --- Filtering & Sorting ---
  public applyFilters(): void {
    let result = [...this.providerGroups];

    if (this.searchText) {
      const search = this.searchText.toLowerCase();
      result = result.filter(g =>
        g.name.toLowerCase().includes(search) ||
        g.site.toLowerCase().includes(search) ||
        g.servicePoints.some(p => p.toLowerCase().includes(search))
      );
    }

    if (this.filterSite) {
      result = result.filter(g => g.site === this.filterSite);
    }

    if (this.sortColumn) {
      result.sort((a: any, b: any) => {
        const valA = a[this.sortColumn];
        const valB = b[this.sortColumn];
        if (typeof valA === 'string') {
          return this.sortDirection === 'asc'
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        }
        return this.sortDirection === 'asc' ? valA - valB : valB - valA;
      });
    }

    this.totalItems = result.length;
    this.filteredGroups = result;
  }

  public sortData(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applyFilters();
  }

  public toggleFilterDropdown(): void {
    this.isFilterDropdownOpen = !this.isFilterDropdownOpen;
  }

  public selectSiteFilter(site: string): void {
    this.filterSite = site;
    this.isFilterDropdownOpen = false;
    this.applyFilters();
    this.currentPage = 1;
  }

  // --- Pagination ---
  public get paginatedGroups(): ProviderGroupSpot[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredGroups.slice(start, start + this.pageSize);
  }

  public get totalPages(): number {
    return Math.ceil(this.totalItems / this.pageSize) || 1;
  }

  public get pagesArray(): number[] {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - 2);
    let end = Math.min(this.totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  public setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  public onPageSizeChange(event: any): void {
    this.pageSize = +event.target.value;
    this.currentPage = 1;
    this.applyFilters();
  }

  // --- Modal Control ---
  public openCreateModal(): void {
    this.selectedGroup = null;
    this.isModalOpen = true;
  }

  public openEditModal(group: ProviderGroupSpot): void {
    this.selectedGroup = { ...group };
    this.isModalOpen = true;
  }

  public closeModal(): void {
    this.isModalOpen = false;
    this.selectedGroup = null;
  }

  public onRefresh(): void {
    this.getProviderGroups();
  }
}
